/**
 * 1.6.2 "Who can run it": the workflow builder's button and dialog for who may start a workflow by hand (access
 * groups and roles, PUT /api/v1/automations/:id/run-access), and the lock pill of the Workflows list. Automatic
 * triggers are not affected; people who manage workflows always may run it.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Lock, Users } from 'lucide-react';
import { api, errorMessage } from '../../api/client';
import { useAccessGroups, useRoles, type RunAccess } from '../../api/hooks';
import { Modal } from '../../components/Modal';
import { MultiSelect } from '../../components/MultiSelect';
import { toast } from '../../components/Toasts';
import { queryKeys } from '../../api/queryKeys';

/** "Promo team, Editors"; a restriction whose groups were all deleted leaves only people who manage workflows. */
export function runAccessLabel(access: RunAccess): string {
  const names = [...access.groups.map((g) => g.name), ...access.roles.map((r) => r.name)];
  return names.length ? names.join(', ') : 'Workflow managers';
}

/** The lock pill on a restricted workflow's row; `mayRun` false adds that the viewer may not start it. */
export function RunAccessPill({ access, mayRun }: { access: RunAccess; mayRun: boolean }) {
  const label = runAccessLabel(access);
  return (
    <span className="pill" data-testid="run-access-pill" data-may-run={mayRun} title={mayRun ? `Only ${label} can run it` : `Only ${label} can run it; you cannot`}>
      <Lock size={11} style={{ marginRight: 4 }} color={mayRun ? undefined : 'var(--warning)'} />{label}
    </span>
  );
}

export function RunAccessButton({ id, access, restricted }: { id: string; access: RunAccess; restricted: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className={`btn ${restricted ? '' : 'ghost'}`} onClick={() => setOpen(true)} data-testid="run-access" title="Who can start this workflow by hand">
        {restricted ? <Lock size={14} color="var(--warning)" /> : <Users size={14} />} {restricted ? runAccessLabel(access) : 'Who can run it'}
      </button>
      {open && <RunAccessModal id={id} access={access} onClose={() => setOpen(false)} />}
    </>
  );
}

function RunAccessModal({ id, access, onClose }: { id: string; access: RunAccess; onClose: () => void }) {
  const groups = useAccessGroups().data?.items ?? [];
  const roles = useRoles().data?.items ?? [];
  const qc = useQueryClient();
  const [only, setOnly] = useState(access.groups.length + access.roles.length > 0);
  const [chosenGroups, setChosenGroups] = useState(new Set(access.groups.map((g) => g.id)));
  const [chosenRoles, setChosenRoles] = useState(new Set(access.roles.map((r) => r.key)));
  const [busy, setBusy] = useState(false);
  const count = chosenGroups.size + chosenRoles.size;
  const names = (all: Array<{ value: string; label: string }>, chosen: Set<string>) => all.filter((o) => chosen.has(o.value)).map((o) => o.label).join(', ') || 'None';
  const groupOptions = groups.map((g) => ({ value: g.id, label: g.name }));
  const roleOptions = roles.map((r) => ({ value: r.key, label: r.name }));
  async function save() {
    setBusy(true);
    try {
      await api(`/api/v1/automations/${id}/run-access`, { method: 'PUT', json: only ? { groups: [...chosenGroups], roles: [...chosenRoles] } : { groups: [], roles: [] } });
      await qc.invalidateQueries({ queryKey: queryKeys.automation(id) });
      void qc.invalidateQueries({ queryKey: queryKeys.automations() });
      void qc.invalidateQueries({ queryKey: ['runnable-workflows'] });
      toast(only && count ? 'Only the chosen groups and roles can run it' : 'Everyone who can run workflows can run it');
      onClose();
    } catch (err) {
      toast(errorMessage(err), 'error');
      setBusy(false);
    }
  }
  return (
    <Modal title="Who can run this workflow" onClose={onClose} actions={<><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn primary" disabled={busy || (only && count === 0) || count > 50} onClick={() => void save()} data-testid="save-run-access">Save</button></>}>
      <p className="muted" style={{ marginTop: 0 }}>Who may start it by hand: from Run workflow on an asset or a sequence, a selection, an assistant or an API key. Its automatic triggers are not affected, and people who manage workflows can always run it. The change applies at once.</p>
      <label className="option-row"><input type="radio" name="run-access" checked={!only} onChange={() => setOnly(false)} data-testid="run-access-everyone" /> Everyone who can run workflows</label>
      <label className="option-row"><input type="radio" name="run-access" checked={only} onChange={() => setOnly(true)} data-testid="run-access-only" /> Only these access groups and roles</label>
      {only && (
        <div style={{ display: 'grid', gap: 12, marginTop: 8, paddingLeft: 24 }}>
          <div className="field">
            <label>Access groups</label>
            {groupOptions.length === 0 ? <span className="muted">Create access groups under Administration → Access first.</span> : (
              <MultiSelect options={groupOptions} selected={chosenGroups} onChange={setChosenGroups} summary={names(groupOptions, chosenGroups)} label="Access groups" testId="run-access-groups" />
            )}
          </div>
          <div className="field">
            <label>Roles</label>
            <MultiSelect options={roleOptions} selected={chosenRoles} onChange={setChosenRoles} summary={names(roleOptions, chosenRoles)} label="Roles" testId="run-access-roles" />
          </div>
          {count === 0 && <span className="help">Choose at least one group or role.</span>}
          {count > 50 && <span className="help warn-text">At most 50 groups and roles together.</span>}
        </div>
      )}
    </Modal>
  );
}
